<template>
  <div class="flex flex-col h-full w-full min-w-0 min-h-0">
  <!-- Loading skeleton -->
  <div
    v-if="applicationsStore.isLoadingKanban"
    class="grid h-full gap-4 px-2 py-1 overflow-x-auto w-full min-w-0"
    :style="{ gridTemplateColumns: `repeat(${KANBAN_COLUMNS.length}, minmax(16rem, 1fr))` }"
  >
    <div
      v-for="col in KANBAN_COLUMNS"
      :key="col.id"
      class="min-h-0 min-w-0 rounded-xl border border-gray-200 bg-white overflow-hidden flex flex-col"
    >
      <div :class="['px-4 py-3 border-b border-gray-100', col.headerBg]">
        <div class="h-4 bg-gray-200 rounded w-24 animate-pulse" />
      </div>
      <div class="p-3 flex flex-col gap-3">
        <div v-for="n in 3" :key="n" class="h-20 bg-gray-100 rounded-lg animate-pulse" />
      </div>
    </div>
  </div>

  <!-- Board -->
  <div
    v-else
    class="grid h-full gap-2 px-2 py-2 overflow-x-auto w-full min-w-0 min-h-0 flex-1"
    :style="{ gridTemplateColumns: `repeat(${visibleColumns.length}, minmax(16rem, 1fr))` }"
  >
    <div
      v-for="col in visibleColumns"
      :key="col.id"
      class="min-h-0 min-w-0 rounded-xl border border-gray-200 bg-white overflow-hidden flex flex-col transition-all duration-200"
      :class="{ 'ring-2 ring-cosmicPurple/40': dragOverColumnId === col.id }"
    >
      <!-- Column header -->
      <div :class="['px-4 py-3 border-b border-gray-100 flex items-center justify-between', col.headerBg]">
        <span :class="['text-sm font-semibold', col.textColor]">{{ col.label }}</span>
        <span :class="['text-xs font-medium px-2 py-0.5 rounded-full', col.badgeBg, col.textColor]">
          {{ columnApps(col).length }}
        </span>
      </div>

      <!-- Draggable acts as the scroll container so it always fills the column -->
        <draggable
          :model-value="columnApps(col)"
          group="kanban-apps"
          item-key="id"
          :animation="150"
          :force-fallback="true"
          :fallback-on-body="true"
          ghost-class="kanban-ghost"
          chosen-class="kanban-chosen"
          drag-class="kanban-drag"
          :data-col-id="col.id"
          class="flex-1 overflow-y-auto min-h-0 p-3 flex flex-col gap-3"
          @start="onDragStart"
          @end="onDragEnd"
          @dragover.prevent
        >
          <template #item="{ element: app }">
            <button
              :data-app-id="app.id"
              @click="navigateTo(app)"
              class="w-full text-left bg-white border border-gray-200 rounded-lg px-3 py-3 hover:shadow-md hover:border-gray-300 transition-[box-shadow,border-color] duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-cosmicPurple/50 cursor-pointer"
              :class="{ 'border-blue-400 ring-1 ring-blue-400': isSelected(app) }"
            >
                <!-- Company logo + name -->
                <div class="flex items-start gap-2 mb-2">
                  <img
                    v-if="app.open_role_details?.employer_details?.logo"
                    :src="app.open_role_details.employer_details.logo"
                    :alt="app.open_role_details.employer_details.name"
                    class="w-8 h-8 rounded-md object-contain flex-shrink-0 mt-0.5 border border-gray-100"
                  />
                  <span
                    v-else
                    class="w-8 h-8 rounded-md bg-gray-100 flex-shrink-0 mt-0.5 flex items-center justify-center"
                  >
                    <Building class="w-4 h-4 text-gray-400" />
                  </span>

                  <div class="flex-1 min-w-0">
                    <p class="text-sm font-medium text-gray-900 leading-tight truncate">
                      {{ app.open_role_details?.role_name || '—' }}
                    </p>
                    <p class="text-xs text-gray-500 truncate mt-0.5">
                      {{ app.open_role_details?.employer_details?.name || '—' }}
                    </p>
                  </div>
                </div>

                <!-- Status badges -->
                <div class="flex flex-wrap gap-1.5 mt-2">
                  <span
                    v-if="app.interview_status && app.interview_status !== 'NOT_STARTED'"
                    :class="['inline-flex items-center text-xs px-2 py-0.5 rounded-full font-medium',
                      interviewStatusStyle(app.interview_status)]"
                  >
                    {{ interviewStatusLabel(app.interview_status) }}
                  </span>
                  <span
                    v-if="!isLimitedUser && app.referral_status && app.referral_status !== 'NOT_STARTED'"
                    :class="['inline-flex items-center text-xs px-2 py-0.5 rounded-full font-medium',
                      referralStatusStyle(app.referral_status)]"
                  >
                    {{ referralStatusLabel(app.referral_status) }}
                  </span>
                </div>

                <!-- Updated date -->
                <p class="text-xs text-gray-400 mt-2 flex items-center gap-1">
                  <Calendar class="w-3 h-3" />
                  {{ formatDate(app.updated_at) }}
                </p>
            </button>
          </template>

          <!-- Empty drop zone hint -->
          <template #footer>
            <div
              v-if="columnApps(col).length === 0"
              class="flex flex-col items-center justify-center py-8 text-gray-400 text-xs gap-1"
            >
              <InboxIcon class="w-6 h-6 opacity-40" />
              <span>No applications</span>
            </div>
          </template>
        </draggable>
    </div>
  </div>

  <!-- Status picker — Tracking / Closed, or interview stage when dropping into Interviewing from elsewhere -->
  <Dialog :open="!!pendingPick" @update:open="(v) => !v && cancelPick()">
    <DialogContent class="max-w-sm">
      <DialogHeader>
        <DialogTitle>{{ pendingPick?.title }}</DialogTitle>
        <DialogDescription>
          {{ pendingPick?.roleName }} at {{ pendingPick?.companyName }}
        </DialogDescription>
      </DialogHeader>
      <div class="flex flex-col gap-2 mt-2">
        <button
          v-for="opt in pendingPick?.options ?? []"
          :key="opt.value"
          @click="confirmPick(opt.value)"
          class="text-left px-4 py-2.5 rounded-lg border border-gray-200 text-sm hover:bg-gray-50 hover:border-gray-300 transition-all"
        >
          <span class="font-medium">{{ opt.label }}</span>
          <span class="block text-xs text-gray-500 mt-0.5">{{ opt.description }}</span>
        </button>
      </div>
      <DialogFooter class="mt-2">
        <button @click="cancelPick()" class="text-sm text-gray-500 hover:text-gray-700">Cancel</button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { Building, Calendar, Inbox as InboxIcon } from 'lucide-vue-next'
// Source import: Vite’s esbuild pre-bundle of the UMD build can leave `this.$slots`
// undefined in dev (breaks getSlot → "reading 'header'"). Production Rollup often works anyway.
import draggable from 'vuedraggable/src/vuedraggable.js'
import { useApplicationsStore, type KanbanCardPatch } from '@/stores/applications'
import { useAuth } from '@/composables/useAuth'
import { formatDate } from '@/utils/formatDate'
import {
  applicationStatusOptions,
  interviewStatusOptions,
  referralStatusOptions
} from '@/const/applicationStatuses'
import { toast } from '@/components/ui/toast/use-toast'
import { useOfferNegotiationModal } from '@/composables/useOfferNegotiationModal'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter
} from '@/components/ui/dialog'
import type { Application } from '@/types/application'

// ─── Column definitions ───────────────────────────────────────────────────────

interface KanbanColumn {
  id: string
  label: string
  headerBg: string
  badgeBg: string
  textColor: string
}

/** Interview stage not started (or unset) — same column semantics as API NOT_STARTED */
function interviewStageNotStarted(app: Application): boolean {
  const i = app.interview_status
  return i == null || i === 'NOT_STARTED'
}

const CLOSED_APPLICATION_STATUSES = [
  'REJECTED',
  'NO_RESPONSE',
  'CANCELLED',
  'POSITION_CLOSED'
] as const

function isClosedKanbanApp(app: Application): boolean {
  const s = app.application_status
  return s != null && (CLOSED_APPLICATION_STATUSES as readonly string[]).includes(s)
}

function isTrackingKanbanApp(app: Application): boolean {
  const s = app.application_status
  if (s == null || s === 'ON_HOLD') return true
  // "Not started" but already in resume review → Applied column, not Tracking
  if (s === 'NOT_STARTED') return app.interview_status !== 'RESUME_STAGE'
  return false
}

/** Signed offer, or in offer negotiation in the interview pipeline (any application_status). */
function isOfferKanbanApp(app: Application): boolean {
  return (
    app.application_status === 'OFFER_SIGNED' ||
    app.interview_status === 'OFFER_NEGOTIATION_STAGE'
  )
}

/** Applied: ongoing + no interview yet, or resume stage while still in "Not started" / Ongoing application flow */
function isAppliedKanbanApp(app: Application): boolean {
  if (app.interview_status === 'RESUME_STAGE') {
    const s = app.application_status
    return s === 'ONGOING' || s === 'NOT_STARTED'
  }
  return app.application_status === 'ONGOING' && interviewStageNotStarted(app)
}

/** Ongoing with interview progress (except resume — that stays in Applied until stage passed), or stage passed — except offer-negotiation (Offer column) */
function isInterviewingKanbanApp(app: Application): boolean {
  if (app.interview_status === 'OFFER_NEGOTIATION_STAGE') return false
  if (app.interview_status === 'RESUME_STAGE' && app.application_status === 'STAGE_PASSED') return true
  const s = app.application_status
  if (s === 'STAGE_PASSED') return true
  if (s === 'ONGOING') {
    if (app.interview_status === 'RESUME_STAGE') return false
    return !interviewStageNotStarted(app)
  }
  return false
}

/**
 * Resolve a single column per app (priority order must stay mutually exclusive).
 * Closed, then offer (incl. offer-negotiation interview), then tracking, etc.
 */
function getKanbanColumnId(app: Application): string | null {
  if (isClosedKanbanApp(app)) return 'closed'
  if (isOfferKanbanApp(app)) return 'offer'
  if (isTrackingKanbanApp(app)) return 'tracking'
  if (isAppliedKanbanApp(app)) return 'applied'
  if (isInterviewingKanbanApp(app)) return 'progressing'
  return null
}

const KANBAN_COLUMNS: KanbanColumn[] = [
  {
    id: 'tracking',
    label: 'Tracking',
    headerBg: 'bg-gray-50',
    badgeBg: 'bg-gray-200',
    textColor: 'text-gray-600'
  },
  {
    id: 'applied',
    label: 'Applied',
    headerBg: 'bg-blue-50',
    badgeBg: 'bg-blue-100',
    textColor: 'text-blue-700'
  },
  {
    id: 'progressing',
    label: 'Interviewing',
    headerBg: 'bg-lime-50',
    badgeBg: 'bg-lime-200',
    textColor: 'text-green-700'
  },
  {
    id: 'offer',
    label: 'Offer',
    headerBg: 'bg-purple-50',
    badgeBg: 'bg-purple-100',
    textColor: 'text-purple-700'
  },
  {
    id: 'closed',
    label: 'Closed',
    headerBg: 'bg-red-50',
    badgeBg: 'bg-red-100',
    textColor: 'text-red-600'
  }
]

/** PATCH when dropping on a column that commits without a picker (not tracking/closed). */
/** Drops on Interviewing always use the interview-stage dialog (ongoing + chosen stage). */
/** Drops on Offer use the offer signed vs offer negotiation dialog. */
function kanbanPatchForColumnId(columnId: string): KanbanCardPatch {
  switch (columnId) {
    case 'applied':
      return { application_status: 'ONGOING', interview_status: 'NOT_STARTED' }
    default:
      return { application_status: 'NOT_STARTED' }
  }
}

// Sub-status options shown in the inline picker for multi-status columns
interface PickOption { value: string; label: string; description: string }

const COLUMN_PICK_OPTIONS: Record<string, { title: string; options: PickOption[] }> = {
  tracking: {
    title: 'How do you want to track this?',
    options: [
      { value: 'NOT_STARTED', label: 'Not started',    description: "I haven't done anything with this yet" },
      { value: 'ON_HOLD',     label: 'On hold',        description: 'Pausing this for now, will revisit later' },
    ]
  },
  closed: {
    title: 'Why is this application closing?',
    options: [
      { value: 'REJECTED',        label: 'Rejected',        description: 'The company rejected my application' },
      { value: 'NO_RESPONSE',     label: 'No response',     description: 'Never heard back from them' },
      { value: 'CANCELLED',       label: 'Withdrew',        description: 'I decided to cancel my application' },
      { value: 'POSITION_CLOSED', label: 'Position closed', description: 'The role was filled or removed' },
    ]
  },
  offer: {
    title: 'How should we record this offer?',
    options: [
      {
        value: 'OFFER_SIGNED',
        label: 'Offer signed',
        description:
          applicationStatusOptions.find((o) => o.value === 'OFFER_SIGNED')?.text ?? 'Congratulations!'
      },
      {
        value: 'OFFER_NEGOTIATION_STAGE',
        label: 'Offer negotiation stage',
        description:
          interviewStatusOptions.find((o) => o.value === 'OFFER_NEGOTIATION_STAGE')?.text ??
          'The offer negotiation stage has started'
      }
    ]
  }
}

/** Interview stages when moving into Interviewing from another column (resume = Applied; offer negotiation = Offer column). */
const KANBAN_INTERVIEW_STAGE_PICK_OPTIONS: PickOption[] = interviewStatusOptions
  .filter(
    (o) =>
      o.value !== 'NOT_STARTED' &&
      o.value !== 'RESUME_STAGE' &&
      o.value !== 'OFFER_NEGOTIATION_STAGE'
  )
  .map((o) => ({
    value: o.value,
    label: o.label,
    description: o.text ?? ''
  }))

// ─── State ────────────────────────────────────────────────────────────────────

const applicationsStore = useApplicationsStore()
const router = useRouter()
const route = useRoute()
const { isLimitedUser } = useAuth()
const { showModal: showOfferNegotiationModal } = useOfferNegotiationModal()

// Highlights the column a card is being dragged over
const dragOverColumnId = ref<string | null>(null)

// Holds data for the inline picker (Tracking / Closed / Interviewing interview stage)
interface PendingPick {
  appId: number
  roleName: string
  companyName: string
  title: string
  options: PickOption[]
  /** application_status only (tracking/closed) vs Ongoing + interview_status (drop into Interviewing) vs Offer column */
  patchKind: 'application_status' | 'ongoing_interview_stage' | 'offer_column'
}
const pendingPick = ref<PendingPick | null>(null)

// ─── Column visibility ────────────────────────────────────────────────────────

const selectedApp = computed<Application | undefined>(() =>
  route.params.application_id
    ? applicationsStore.kanbanApplications.find(
        (app) => String(app.id) === String(route.params.application_id)
      )
    : undefined
)

const activeColumnId = computed<string | null>(() => {
  if (!selectedApp.value) return null
  return getKanbanColumnId(selectedApp.value)
})

const visibleColumns = computed<KanbanColumn[]>(() => {
  if (!route.params.application_id || !activeColumnId.value) return KANBAN_COLUMNS
  return KANBAN_COLUMNS.filter((col) => col.id === activeColumnId.value)
})

// ─── Column / card helpers ────────────────────────────────────────────────────

function columnApps(col: KanbanColumn): Application[] {
  return applicationsStore.kanbanApplications.filter((app) => getKanbanColumnId(app) === col.id)
}

function isSelected(app: Application) {
  return String(route.params.application_id) === String(app.id)
}

function navigateTo(app: Application) {
  if (isSelected(app)) {
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    router.push({ name: 'User Applications', query: { ...route.query } })
    return
  }
  // eslint-disable-next-line @typescript-eslint/no-floating-promises
  router.push({
    name: 'User Application container',
    params: { application_id: String(app.id) },
    query: { ...route.query }
  })
}

// ─── Drag and drop ────────────────────────────────────────────────────────────

function onDragStart() {
  document.body.classList.add('is-dragging')
}

async function onDragEnd(event: any) {
  document.body.classList.remove('is-dragging')
  dragOverColumnId.value = null

  const toColId: string | null = event.to?.getAttribute('data-col-id') ?? null
  const fromColId: string | null = event.from?.getAttribute('data-col-id') ?? null

  // Same column — no status change needed
  if (!toColId || toColId === fromColId) return

  const appId = Number(event.item?.getAttribute('data-app-id'))
  if (!appId) return

  // Multi-status columns show a picker before committing
  const pickConfig = COLUMN_PICK_OPTIONS[toColId]
  if (pickConfig) {
    const app = applicationsStore.kanbanApplications.find((a) => a.id === appId)
    const patchKind = toColId === 'offer' ? 'offer_column' : 'application_status'
    pendingPick.value = {
      appId,
      roleName: app?.open_role_details?.role_name ?? 'this role',
      companyName: app?.open_role_details?.employer_details?.name ?? 'this company',
      title: pickConfig.title,
      options: pickConfig.options,
      patchKind
    }
    return
  }

  if (toColId === 'progressing' && fromColId !== 'progressing') {
    const app = applicationsStore.kanbanApplications.find((a) => a.id === appId)
    pendingPick.value = {
      appId,
      roleName: app?.open_role_details?.role_name ?? 'this role',
      companyName: app?.open_role_details?.employer_details?.name ?? 'this company',
      title: 'Which interview stage are you in?',
      options: KANBAN_INTERVIEW_STAGE_PICK_OPTIONS,
      patchKind: 'ongoing_interview_stage'
    }
    return
  }

  await applyKanbanPatch(appId, kanbanPatchForColumnId(toColId))
}

async function applyKanbanPatch(appId: number, patch: KanbanCardPatch) {
  try {
    await applicationsStore.moveKanbanCard(appId, patch)
  } catch {
    toast({
      title: 'Update failed',
      description: 'Could not move the application. Please try again.',
      variant: 'destructive'
    })
  }
}

async function confirmPick(value: string) {
  if (!pendingPick.value) return
  const { appId, patchKind } = pendingPick.value
  pendingPick.value = null
  if (patchKind === 'ongoing_interview_stage') {
    await applyKanbanPatch(appId, {
      application_status: 'ONGOING',
      interview_status: value
    })
    if (value === 'OFFER_NEGOTIATION_STAGE') {
      showOfferNegotiationModal(appId)
    }
  } else if (patchKind === 'offer_column') {
    if (value === 'OFFER_SIGNED') {
      await applyKanbanPatch(appId, { application_status: 'OFFER_SIGNED' })
    } else if (value === 'OFFER_NEGOTIATION_STAGE') {
      await applyKanbanPatch(appId, {
        application_status: 'ONGOING',
        interview_status: 'OFFER_NEGOTIATION_STAGE'
      })
      showOfferNegotiationModal(appId)
    }
  } else {
    await applyKanbanPatch(appId, { application_status: value })
  }
}

function cancelPick() {
  pendingPick.value = null
  // Re-fetch to restore the board to the last known server state
  // (the DOM moved the card temporarily; the store was never updated)
  // eslint-disable-next-line @typescript-eslint/no-floating-promises
  applicationsStore.fetchApplicationsForKanban()
}

// ─── Status label / style helpers ─────────────────────────────────────────────

function interviewStatusLabel(status: string) {
  return interviewStatusOptions.find((o) => o.value === status)?.label ?? status
}

function interviewStatusStyle(status: string) {
  const opt = interviewStatusOptions.find((o) => o.value === status)
  return [opt?.bgColor ?? 'bg-gray-100', opt?.textColor ?? 'text-gray-700']
}

function referralStatusLabel(status: string) {
  return referralStatusOptions.find((o) => o.value === status)?.label ?? status
}

function referralStatusStyle(status: string) {
  const opt = referralStatusOptions.find((o) => o.value === status)
  return [opt?.bgColor ?? 'bg-gray-100', opt?.textColor ?? 'text-gray-700']
}

// ─── Lifecycle ────────────────────────────────────────────────────────────────

onMounted(() => {
  if (applicationsStore.kanbanApplications.length === 0) {
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    applicationsStore.fetchApplicationsForKanban()
  }
})
</script>

<style>
/* Force grabbing cursor on everything while a drag is in progress */
body.is-dragging,
body.is-dragging * {
  cursor: grabbing !important;
}
</style>

<style scoped>
/* Placeholder left in the source column while dragging */
.kanban-ghost {
  opacity: 0.35;
  background: #f3f4f6;
  border-radius: 0.5rem;
}

/* The card being actively held */
.kanban-chosen {
  box-shadow: 0 4px 12px rgb(0 0 0 / 0.12);
  /* Cards use transition-all; Sortable updates transform every frame — animating it causes heavy lag */
  transition: none !important;
}

/* The floating clone following the cursor (forceFallback so CSS cursor applies) */
.kanban-drag {
  cursor: grabbing !important;
  pointer-events: none;
  opacity: 0.95;
  transform: rotate(1.5deg);
  box-shadow: 0 8px 24px rgb(0 0 0 / 0.15);
  transition: none !important;
  will-change: transform;
}
</style>
